import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Path } from '~/routes/constants';
import { ChakraBox, FAIconName, NavItem, Popover } from '@equip.health/ui';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { ChannelId } from '~/lib/constants/chat.constants';
import { useChannelListContext } from '@sendbird/uikit-react/ChannelList/context';
import MessagesMenuBody from './MessagesMenuBody';
import MessagesMenuFooter from './MessagesMenuFooter';
import MessagesMenuHeader from './MessagesMenuHeader';

import './MessagesMenu.css';
import { getUnreadMessagesCount } from '~/lib/util/chat/chat.utils';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { MessagesEventsProps } from '~/analytics/messagesEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import AnalyticsEvents from '~/analytics';

const TOP_LEVEL_ID = 'messages-menu';

interface MessagesMenuProps {
  onMessagesMenuVisibilityChange: (isMessageOver: boolean) => void;
  isSmall?: boolean;
}

const getAllUnreadMessagesCount = (channels: GroupChannel[]) =>
  channels?.reduce((acc, c) => acc + getUnreadMessagesCount(c), 0);

const MessagesMenu = ({
  onMessagesMenuVisibilityChange,
  isSmall = false,
}: MessagesMenuProps) => {
  const history = useHistory();
  const { allChannels } = useChannelListContext();
  const { trackEvent } = useAnalytics();
  const { messagePinning: isMessagePinningEnabled } = useFlags<LDFlags>();

  const pinMessageChannels: GroupChannel[] = allChannels.filter(
    (c) => c.pinnedMessageIds?.length > 0,
  );
  const newMessageChannels: GroupChannel[] = allChannels.filter(
    (c) => getUnreadMessagesCount(c) > 0,
  );
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const newMessagesUnreadCount = getAllUnreadMessagesCount(newMessageChannels);

  const showingChannelsCount = isMessagePinningEnabled
    ? newMessageChannels?.length + pinMessageChannels?.length
    : newMessageChannels?.length;

  const getPatientIdFromChannelUrl = (channelUrl: string) => {
    const splitted = channelUrl.split(/[_]+/g);
    if (channelUrl.startsWith(ChannelId.ACT)) return splitted[1];
    else if (channelUrl.startsWith(ChannelId.PT_PR_CCT)) return splitted[3];
    else return splitted[2];
  };

  const onChannelSelected = (
    channel: GroupChannel,
    isFromPinnedSection: boolean,
  ) => {
    if (channel) {
      const eventProps: MessagesEventsProps = {
        channelType: channel.url.split('_')[0],
        channelUrl: channel.url,
        hasPinnedMessages: (channel.pinnedMessageIds?.length || 0) > 0,
        patientId: getPatientIdFromChannelUrl(channel.url),
        source: AnalyticsSourceValues.MESSAGES_MENU,
        unreadCount: getUnreadMessagesCount(channel),
      };

      const eventName = isFromPinnedSection
        ? AnalyticsEvents.pinnedUnreadMessageChannelListClick
        : AnalyticsEvents.newUnreadMessageChannelListClick;

      trackEvent(eventName, eventProps);

      setIsOpen(false);
      const channelUrl = channel.url;
      const patientId = getPatientIdFromChannelUrl(channelUrl);
      history.push(
        `${Path.MAUD_PATIENTS}/${patientId}/messages?channelUrl=${channelUrl}`,
      );
    } else {
      console.error('no channel found: ', channel);
    }
  };

  const onSectionToggle = (
    sectionType: 'new' | 'pinned',
    isExpanded: boolean,
  ) => {
    const eventProps: MessagesEventsProps = {
      source: AnalyticsSourceValues.MESSAGES_MENU,
    };

    let eventName;
    if (sectionType === 'new') {
      eventName = isExpanded
        ? AnalyticsEvents.newUnreadMessageChannelExpandList
        : AnalyticsEvents.newUnreadMessageChannelCollapseList;
    } else {
      eventName = isExpanded
        ? AnalyticsEvents.pinnedUnreadMessageChannelExpandList
        : AnalyticsEvents.pinnedUnreadMessageChannelCollapseList;
    }

    trackEvent(eventName, eventProps);
  };

  const handleOpen = () => {
    trackEvent(AnalyticsEvents.maudLeftNavBarMessagesTab, {
      source: AnalyticsSourceValues.MAUD_LEFT_NAV,
    });
    setIsOpen(true);
  };

  useEffect(() => {
    onMessagesMenuVisibilityChange(isOpen);
  }, [isOpen]);

  return (
    <Popover
      bodyContent={
        <MessagesMenuBody
          isChannelsLoading={!allChannels}
          isMessagePinningEnabled={isMessagePinningEnabled}
          key="messages-menu-body"
          newMessageChannels={newMessageChannels}
          newMessagesCount={newMessagesUnreadCount}
          onChannelSelected={onChannelSelected}
          onSectionToggle={onSectionToggle}
          pinMessageChannels={pinMessageChannels}
        />
      }
      bodyPadding="0px"
      footerContent={
        <MessagesMenuFooter showingChannelsCount={showingChannelsCount} />
      }
      headerContent={<MessagesMenuHeader />}
      id={TOP_LEVEL_ID}
      isOpen={isOpen}
      margin="0px 0px 20px 0px"
      minWidth="448px"
      onClose={() => setIsOpen(false)}
      onOpen={handleOpen}
      placement="right"
      size="lg"
      triggerContent={
        <ChakraBox
          id={`${TOP_LEVEL_ID}__trigger-content`}
          padding="0px 4px 12px 4px"
        >
          <NavItem
            counter={newMessagesUnreadCount > 0 && newMessagesUnreadCount}
            iconName={FAIconName.messagesRegular}
            isSelected={isOpen}
            isSmall={isSmall}
            label="Messages"
            tooltipText="Messages"
          />
        </ChakraBox>
      }
    />
  );
};

export default MessagesMenu;
